import type { AssignedAgency } from '../../../features/auth/agencyAccess';
import {
  agencyDetailsLink,
  brandDetailsLink,
  campaignDetailsLink,
  campaignListLink,
  type HierarchyLink,
} from '../../../features/campaigns/hierarchyLinks';
import type { MadBuyLineItem } from '../../../features/campaigns/campaigns/lineItemsModel';
import { campaignHierarchyRoute, type BreadcrumbSegment } from '../../../lib/breadcrumbs';
import { dashboardT } from '../../../lib/messages';

export type CampaignHierarchyRoute = NonNullable<ReturnType<typeof campaignHierarchyRoute>>;

interface NamedEntity {
  id: string;
  name: string;
}

export interface CampaignBreadcrumbData {
  name?: string;
  brandId?: string;
  brand?: { id: string; name?: string };
}

export interface BrandBreadcrumbData {
  name?: string;
  agency?: NamedEntity | null;
}

interface ObservedQuery<TData> {
  data: TData | undefined;
  isPending: boolean;
}

export interface CampaignHierarchyCrumbsInput {
  route: CampaignHierarchyRoute;
  /** Agency of the `/a/$agencyId` workspace the user is in, if any. */
  workspaceAgency: AssignedAgency | undefined;
  brandId: string | undefined;
  campaign: ObservedQuery<CampaignBreadcrumbData> | undefined;
  brand: (ObservedQuery<BrandBreadcrumbData> & { isSuccess: boolean }) | undefined;
  agencyDetails: ObservedQuery<NamedEntity> | undefined;
  lineItem: ObservedQuery<MadBuyLineItem> | undefined;
}

/**
 * Resolves the campaign hierarchy route for the current location. Agency workspace routes only
 * resolve when the workspace agency is assigned to the user.
 */
export function resolveCampaignHierarchyScope(
  pathname: string,
  search: Record<string, unknown>,
  agencies: AssignedAgency[],
) {
  const parsed = campaignHierarchyRoute(pathname, search);
  const agency = agencies.find((item) => item.id === parsed?.workspaceAgencyId);
  const route = parsed && (!parsed.workspaceAgencyId || agency) ? parsed : null;
  return { route, agency };
}

export function searchBrandId(search: object): string {
  return 'brand' in search && typeof search.brand === 'string' ? search.brand : '';
}

function linkCrumb(label: string, link: HierarchyLink, loading = false): BreadcrumbSegment {
  return { label, loading, path: `${link.to}?${new URLSearchParams(link.search)}`, ...link };
}

function parentAgencyCrumb({
  route,
  workspaceAgency,
  brandId,
  brand,
  agencyDetails,
}: CampaignHierarchyCrumbsInput): BreadcrumbSegment | null {
  const workspaceAgencyId = workspaceAgency?.id;
  if (workspaceAgency) return { path: 'agency-parent', label: workspaceAgency.name };
  const parentAgency = agencyDetails?.data ?? brand?.data?.agency;
  if (parentAgency)
    return linkCrumb(parentAgency.name, agencyDetailsLink(parentAgency.id, workspaceAgencyId));
  if (route.agencyId)
    return linkCrumb(
      dashboardT('agencyAssignment.label'),
      agencyDetailsLink(route.agencyId, workspaceAgencyId),
      agencyDetails?.isPending,
    );
  if (brandId && !brand?.isSuccess)
    return {
      path: 'agency-parent',
      label: dashboardT('campaignHierarchy.agencyUnavailable'),
      loading: brand?.isPending ?? true,
    };
  return null;
}

function brandCrumb({
  workspaceAgency,
  brandId,
  brand,
  campaign,
}: CampaignHierarchyCrumbsInput): BreadcrumbSegment | null {
  if (!brandId) return null;
  const campaignBrandName = campaign?.data?.brand?.name;
  return linkCrumb(
    brand?.data?.name ?? campaignBrandName ?? dashboardT('campaigns.brand'),
    brandDetailsLink(brandId, workspaceAgency?.id),
    Boolean(brand?.isPending && !campaignBrandName),
  );
}

function campaignCrumb({
  route,
  workspaceAgency,
  campaign,
}: CampaignHierarchyCrumbsInput): BreadcrumbSegment | null {
  if (!route.campaignId) return null;
  return linkCrumb(
    campaign?.data?.name ?? dashboardT('nav.breadcrumb.entity'),
    campaignDetailsLink(route.campaignId, workspaceAgency?.id),
    campaign?.isPending,
  );
}

function lineItemCrumb({
  route,
  lineItem,
}: CampaignHierarchyCrumbsInput): BreadcrumbSegment | null {
  if (route.createLineItem)
    return { label: dashboardT('nav.action.create'), path: 'line-item', loading: false };
  if (!route.lineItemId) return null;
  const data = lineItem?.data;
  const label =
    data?.campaign?.id === route.campaignId
      ? (data?.name ?? dashboardT('nav.breadcrumb.entity'))
      : dashboardT('nav.breadcrumb.entity');
  return { label, path: 'line-item', loading: Boolean(lineItem?.isPending) };
}

/** Builds campaign hierarchy crumbs; the current (last) crumb is never a link. */
export function buildCampaignHierarchyCrumbs(
  input: CampaignHierarchyCrumbsInput,
): BreadcrumbSegment[] {
  const crumbs = [
    linkCrumb(dashboardT('nav.campaigns'), campaignListLink(input.workspaceAgency?.id)),
    parentAgencyCrumb(input),
    brandCrumb(input),
    campaignCrumb(input),
    lineItemCrumb(input),
  ].filter((crumb): crumb is BreadcrumbSegment => crumb !== null);
  const last = crumbs.at(-1);
  if (last) {
    delete last.to;
    delete last.search;
  }
  return crumbs;
}
